import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { apiClient, getEventId, setEventId } from '../api/client';
import { useAuth } from './AuthContext';

/**
 * A tenant's event as returned by GET /events. The picker only needs identity +
 * venue; the full Events page handles the rest.
 */
export interface EventSummary {
  id: string;
  name: string;
  date?: string;
  startTime?: string;
  endTime?: string;
  venueId?: string;
  venue?: { id: string; name: string } | null;
  status?: string;
  roomName?: string | null;
  affiliationLabel?: string | null;
}

interface EventContextValue {
  /** The tenant's events (loaded from GET /events). */
  events: EventSummary[];
  /** The currently selected event id (also mirrored into X-Event-Id). */
  selectedEventId: string | null;
  /** The selected event object, if resolved. */
  selectedEvent: EventSummary | null;
  loading: boolean;
  /**
   * Module keys enabled for the SELECTED event (drives nav + feature gating).
   * Empty when no event is selected. Loaded from GET /events/:id/modules.
   */
  enabledModules: string[];
  /** True while the selected event's modules are loading. */
  modulesLoading: boolean;
  /** Reload the selected event's enabled modules (after a toggle). */
  refreshModules: () => Promise<void>;
  /** Select an event — persists X-Event-Id and triggers page re-scope. */
  selectEvent: (id: string | null) => void;
  /** Reload the event list from the server. */
  refresh: () => Promise<void>;
  /** Quick-create an event (name + optional venue + type + room) and select it. */
  createEvent: (input: { name: string; venueId?: string; eventType?: string; roomName?: string }) => Promise<EventSummary>;
}

const EventContext = createContext<EventContextValue | null>(null);

export function EventProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [events, setEvents] = useState<EventSummary[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(getEventId());
  const [loading, setLoading] = useState(false);
  const [enabledModules, setEnabledModules] = useState<string[]>([]);
  const [modulesLoading, setModulesLoading] = useState(false);
  // Guard so we only auto-select the first event once per load.
  const autoSelectedRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient<EventSummary[]>('/events');
      const list = Array.isArray(data) ? data : [];
      setEvents(list);

      // Reconcile the selection: keep the current one if it still exists,
      // otherwise fall back to the sessionStorage value, otherwise the first
      // event. This keeps the picker sane across reloads and deletes.
      const current = getEventId();
      const stillValid = current && list.some((e) => e.id === current);
      if (stillValid) {
        setSelectedEventId(current);
      } else if (!autoSelectedRef.current && list.length > 0) {
        autoSelectedRef.current = true;
        setEventId(list[0].id);
        setSelectedEventId(list[0].id);
      } else if (list.length === 0) {
        setEventId(null);
        setSelectedEventId(null);
      }
    } catch {
      // Non-fatal: the picker just shows no events. Pages fall back to the
      // single-event (no X-Event-Id) behavior.
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load events once the user is authenticated (so the request carries a token
  // and the tenant/event scoping resolves correctly).
  useEffect(() => {
    if (!isAuthenticated) {
      setEvents([]);
      setSelectedEventId(null);
      autoSelectedRef.current = false;
      return;
    }
    load();
  }, [isAuthenticated, load]);

  const loadModules = useCallback(async (eventId: string | null) => {
    if (!eventId) {
      setEnabledModules([]);
      return;
    }
    setModulesLoading(true);
    try {
      const res = await apiClient<{ modules: { key: string; enabled: boolean }[] }>(
        `/events/${eventId}/modules`,
      );
      setEnabledModules((res.modules || []).filter((m) => m.enabled).map((m) => m.key));
    } catch {
      // Fail open-ish: no module data -> show nothing module-gated. Core nav
      // (Dashboard/Events/Settings) has no module and always shows.
      setEnabledModules([]);
    } finally {
      setModulesLoading(false);
    }
  }, []);

  // Load the selected event's modules whenever the selection changes.
  useEffect(() => {
    if (!isAuthenticated) { setEnabledModules([]); return; }
    loadModules(selectedEventId);
  }, [isAuthenticated, selectedEventId, loadModules]);

  const refreshModules = useCallback(async () => {
    await loadModules(selectedEventId);
  }, [loadModules, selectedEventId]);

  const selectEvent = useCallback((id: string | null) => {
    setEventId(id);
    setSelectedEventId(id);
  }, []);

  const refresh = useCallback(async () => {
    await load();
  }, [load]);

  const createEvent = useCallback(
    async (input: { name: string; venueId?: string; eventType?: string; roomName?: string }) => {
      const created = await apiClient<EventSummary>('/events/quick-create', {
        method: 'POST',
        body: input,
      });
      await load();
      // Select the freshly created event so pages immediately scope to it.
      setEventId(created.id);
      setSelectedEventId(created.id);
      return created;
    },
    [load],
  );

  const selectedEvent =
    events.find((e) => e.id === selectedEventId) ?? null;

  const value: EventContextValue = {
    events,
    selectedEventId,
    selectedEvent,
    loading,
    enabledModules,
    modulesLoading,
    refreshModules,
    selectEvent,
    refresh,
    createEvent,
  };

  return <EventContext.Provider value={value}>{children}</EventContext.Provider>;
}

export function useEvents(): EventContextValue {
  const ctx = useContext(EventContext);
  if (!ctx) {
    throw new Error('useEvents must be used within an EventProvider');
  }
  return ctx;
}
